import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Info,
  ArrowUp,
  ArrowDown,
  ArrowLeftRight,
  AlertTriangle,
  OctagonAlert,
  Ban,
  Check,
  CircleHelp,
  Loader2,
} from "lucide-react";
import { ComboInfoSheet } from "@/components/ComboInfoSheet";
import { useSubstanceIdMap } from "@/hooks/useSubstanceIdMap";
import { useMatrixData, findInteraction, type Level, type Marker, type MatrixInteraction } from "@/hooks/useMatrixData";
import { useT } from "@/i18n/LanguageContext";
import type { StringKey } from "@/i18n/strings";
import { PageSources } from "@/components/SourcesSheet";
import { InteractionAiAnswer } from "@/components/InteractionAiAnswer";
import { buildMeta } from "@/lib/seo";

export const Route = createFileRoute("/matrix")({
  head: () =>
    buildMeta({
      title: "Interaction Matrix (Wechselwirkungen)",
      description:
        "Interaktionsmatrix für 29 psychoaktive Substanzen: Kombinations-Risiken, Serotonin- und Atem-Warnungen auf einen Blick.",
      path: "/matrix",
    }),
  component: MatrixPage,
});

type Interaction = MatrixInteraction;

const MARKER_EMOJI: Record<Marker, string> = {
  serotonin: "🧠",
  cardio: "❤️",
  serotonin_cardio: "💥",
  respiratory: "☠️",
};
const MARKER_CHIP_BG: Record<Marker, string> = {
  serotonin: "#be185d",
  cardio: "#ea580c",
  serotonin_cardio: "#dc2626",
  respiratory: "#0a0a0a",
};
const MARKER_CHIP_BORDER: Record<Marker, string> = {
  serotonin: "rgba(255,255,255,0.25)",
  cardio: "rgba(255,255,255,0.25)",
  serotonin_cardio: "rgba(255,255,255,0.3)",
  respiratory: "rgba(255,255,255,0.4)",
};
const MARKER_ORDER: Marker[] = ["serotonin", "cardio", "serotonin_cardio", "respiratory"];
const ROW_LABEL_WIDTH = 120;
const COLUMN_LABEL_OFFSET_X = 11;

function MarkerChip({ m, size = 18 }: { m: Marker; size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        borderRadius: "50%",
        background: MARKER_CHIP_BG[m],
        border: `1px solid ${MARKER_CHIP_BORDER[m]}`,
        fontSize: Math.round(size * 0.7),
        lineHeight: 1,
        boxShadow: "0 0 2px rgba(0,0,0,0.6)",
      }}
    >
      {MARKER_EMOJI[m]}
    </span>
  );
}

function normalizeMarkers(markers?: Marker[]): Marker[] {
  if (!markers || !markers.length) return [];
  const set = new Set(markers);
  if (set.has("serotonin") && set.has("cardio")) {
    set.delete("serotonin");
    set.delete("cardio");
    set.add("serotonin_cardio");
  }
  return MARKER_ORDER.filter((m) => set.has(m));
}

const LEVEL_COLORS: Record<Level, string> = {
  safe: "#3FB68B",
  synergy: "#4A90D9",
  reduced: "#8A94A6",
  caution: "#E0B73A",
  highrisk: "#DB8B3A",
  contra: "#C0455B",
  unknown: "#6B7280",
};

/** Neutral hatched fill so "unknown" is never read as a green all-clear. */
const UNKNOWN_HATCH =
  "repeating-linear-gradient(135deg, rgba(255,255,255,0.16) 0 3px, rgba(255,255,255,0.03) 3px 7px)";

/** Shared pattern classes so cell and legend carry the same non-color cue. */
const LEVEL_PATTERN: Record<Level, string> = {
  safe: "rl-risk-pattern rl-risk-safe",
  synergy: "rl-risk-pattern rl-risk-synergy",
  reduced: "rl-risk-pattern rl-risk-reduced",
  caution: "rl-risk-pattern rl-risk-caution",
  highrisk: "rl-risk-pattern rl-risk-highrisk",
  contra: "rl-risk-pattern rl-risk-contra",
  unknown: "rl-risk-pattern rl-risk-unknown",
};

const LEVEL_ICONS: Record<Level, typeof ArrowUp> = {
  safe: Check,
  synergy: ArrowUp,
  reduced: ArrowDown,
  caution: AlertTriangle,
  highrisk: OctagonAlert,
  contra: Ban,
  unknown: CircleHelp,
};

function MatrixPage() {
  const t = useT();
  const { substances, interactions, loading, loadError, invalidRiskValues, unresolvedRows } = useMatrixData();
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeCell, setActiveCell] = useState<{ r: number; c: number }>({ r: 0, c: 1 });
  /** Last valid pair, kept after the detail sheet closes so the question form stays usable. */
  const [aiPair, setAiPair] = useState<{ a: string; b: string } | null>(null);
  const idMap = useSubstanceIdMap();

  const lookup = useMemo(() => {
    const m = new Map<string, { level: Level; markers: Marker[] }>();
    for (const i of interactions) {
      const entry = { level: i.level, markers: normalizeMarkers(i.markers) };
      m.set(`${i.a}|${i.b}`, entry);
      m.set(`${i.b}|${i.a}`, entry);
    }
    return m;
  }, [interactions]);

  const labelFor = (lvl: Level) => t(`level.${lvl}` as StringKey);

  const selected = findInteraction(interactions, a, b);
  const hasPair = Boolean(a && b && a !== b);
  /** Missing off-diagonal pair: shown as unknown, never as low risk. */
  const noAssessment = hasPair && !selected;
  const selectedLevel: Level | null = hasPair ? selected?.level ?? "unknown" : null;
  const selectedLabel = noAssessment
    ? t("matrix.noAssessment")
    : selectedLevel
      ? labelFor(selectedLevel)
      : "";

  const selectStyle: React.CSSProperties = {
    background: "var(--bg-surface)",
    color: "var(--text-primary)",
    border: "1px solid var(--border-purple)",
    borderRadius: 6,
    padding: "0.4rem 0.75rem",
    fontSize: "0.9rem",
  };

  const clearTimerRef = useRef<number | null>(null);
  const interactiveAreaRef = useRef<HTMLDivElement>(null);
  const SHEET_ANIMATION_MS = 500;

  useEffect(() => {
    if (a && b && a !== b) {
      if (clearTimerRef.current) {
        window.clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }
      setSheetOpen(true);
      setAiPair({ a, b });
    } else {
      setSheetOpen(false);
    }
  }, [a, b]);

  useEffect(() => {
    return () => {
      if (clearTimerRef.current) window.clearTimeout(clearTimerRef.current);
    };
  }, []);

  const handleSheetOpenChange = (open: boolean) => {
    setSheetOpen(open);
    if (!open) {
      if (clearTimerRef.current) window.clearTimeout(clearTimerRef.current);
      clearTimerRef.current = window.setTimeout(() => {
        setA("");
        setB("");
        clearTimerRef.current = null;
      }, SHEET_ANIMATION_MS);
    }
  };

  // Overlay handles its own outside-click/backdrop dismissal.


  function pickPair(row: string, col: string) {
    if (row === col) return;
    setA(row);
    setB(col);
  }

  /** Roving tabindex: one cell is tabbable, arrow keys move between cells. */
  function focusCell(r: number, c: number) {
    const el = document.querySelector<HTMLButtonElement>(`[data-cell="${r}-${c}"]`);
    el?.focus();
    setActiveCell({ r, c });
  }

  function handleCellKeyDown(e: React.KeyboardEvent, r: number, c: number) {
    const last = substances.length - 1;
    const step = (dr: number, dc: number) => {
      let nr = r;
      let nc = c;
      for (let i = 0; i <= last + 1; i++) {
        nr = Math.min(last, Math.max(0, nr + dr));
        nc = Math.min(last, Math.max(0, nc + dc));
        if (nr === r && nc === c) return;
        if (nr !== nc) break;
      }
      if (nr !== nc) focusCell(nr, nc);
    };
    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        step(0, 1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        step(0, -1);
        break;
      case "ArrowDown":
        e.preventDefault();
        step(1, 0);
        break;
      case "ArrowUp":
        e.preventDefault();
        step(-1, 0);
        break;
      case "Home":
        e.preventDefault();
        focusCell(r, r === 0 ? 1 : 0);
        break;
      case "End":
        e.preventDefault();
        focusCell(r, r === last ? last - 1 : last);
        break;
      default:
        break;
    }
  }

  if (loadError) {
    return (
      <div data-section="matrix">
        <h1 className="page-title">{t("matrix.title")}</h1>
        <div className="page-title-bar" />
        <div
          role="alert"
          className="glass-card"
          style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "1rem 1.5rem", color: "var(--text-secondary, var(--text-primary))", fontSize: "0.9rem" }}
        >
          <AlertTriangle size={16} style={{ color: "var(--accent-cyan)", flexShrink: 0 }} aria-hidden />
          <span>{t("matrix.loadError")}</span>
        </div>
      </div>
    );
  }

  if (loading && substances.length === 0) {
    return (
      <div data-section="matrix">
        <h1 className="page-title">{t("matrix.title")}</h1>
        <div className="page-title-bar" />
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--text-muted)", fontSize: "0.85rem", padding: "1.5rem 0" }}>
          <Loader2 size={16} className="animate-spin" />
          <span>lädt</span>
        </div>
      </div>
    );
  }

  return (
    <div data-section="matrix">
      <h1 className="page-title">{t("matrix.title")}</h1>
      <div className="page-title-bar" />





      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem 1.25rem", marginBottom: "1.5rem" }}>
        {(Object.keys(LEVEL_COLORS) as Level[]).map((lvl) => (
          <div key={lvl} style={{ display: "flex", gap: "0.4rem", alignItems: "center", fontSize: "0.8rem", color: "var(--text-muted)" }}>
            <span
              aria-hidden
              className={LEVEL_PATTERN[lvl]}
              style={{
                width: 16,
                height: 16,
                borderRadius: 3,
                background: LEVEL_COLORS[lvl],
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(255,255,255,0.92)",
                flexShrink: 0,
              }}
            >
              {(() => {
                const Icon = LEVEL_ICONS[lvl];
                return <Icon size={11} strokeWidth={2.5} />;
              })()}
            </span>
            <span>{labelFor(lvl)}</span>
          </div>
        ))}
      </div>

      {/* Safety note for the risk taxonomy */}
      <div
        style={{
          marginBottom: "1.5rem",
          fontSize: "0.78rem",
          lineHeight: 1.5,
          color: "var(--text-secondary, var(--text-primary))",
          borderLeft: "2px solid var(--accent-border)",
          paddingLeft: "0.75rem",
        }}
      >
        {t("matrix.legendNote")}
      </div>

      {/* Data validation warning: unmapped enum values / unresolved substances */}
      {(invalidRiskValues.length > 0 || unresolvedRows > 0) && (
        <div
          role="status"
          data-testid="matrix-data-warning"
          className="glass-card"
          style={{
            marginBottom: "1.5rem",
            padding: "0.75rem 1rem",
            display: "flex",
            alignItems: "flex-start",
            gap: "0.6rem",
            fontSize: "0.8rem",
            lineHeight: 1.5,
            color: "var(--text-secondary, var(--text-primary))",
          }}
        >
          <AlertTriangle size={15} style={{ color: LEVEL_COLORS.unknown, flexShrink: 0, marginTop: 2 }} aria-hidden />
          <span>
            <strong style={{ fontWeight: 600 }}>{t("matrix.dataWarning.title")}: </strong>
            {invalidRiskValues.length > 0 &&
              t("matrix.dataWarning.invalid").replace("{n}", String(invalidRiskValues.length)) + " "}
            {unresolvedRows > 0 &&
              t("matrix.dataWarning.unresolved").replace("{n}", String(unresolvedRows))}
          </span>
        </div>
      )}

      {/* Mechanistic markers legend */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          marginBottom: "1.5rem",
          padding: "0.75rem 1rem",
          border: "1px solid var(--border-purple)",
          borderRadius: 8,
          background: "rgba(255,255,255,0.02)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-secondary, var(--text-primary))" }}>
            {t("marker.legend.title")}
          </span>
          <span style={{ fontSize: "0.78rem", color: "var(--text-secondary, var(--text-primary))" }}>
            {t("marker.legend.intro")}
          </span>
        </div>
        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontStyle: "italic" }}>
          {t("marker.legend.maoi")}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem 1.25rem" }}>
          {MARKER_ORDER.map((m) => (
            <div key={m} style={{ display: "flex", gap: "0.4rem", alignItems: "center", fontSize: "0.8rem", color: "var(--text-secondary, var(--text-primary))" }}>
              <MarkerChip m={m} size={20} />
              <span>{t(`marker.${m}` as StringKey)}</span>
            </div>
          ))}
        </div>
      </div>


      {/* Interaktiver Bereich (Quick-Check, Matrix, Sheet) - Klicks außerhalb schließen das Panel */}
      <div ref={interactiveAreaRef}>

      {/* Quick-Check Card (always render container so dropdowns stay in place) */}
      <div className="glass-card" style={{ padding: "1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
        {/* Selector */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", alignItems: "center" }}>
          <select aria-label={t("matrix.pickSubstance") + " A"} value={a} onChange={(e) => setA(e.target.value)} style={selectStyle}>
            <option value="">{t("matrix.pickSubstance")}</option>
            {substances.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span style={{ color: "var(--text-muted)", fontSize: "1.1rem" }}>+</span>
          <select aria-label={t("matrix.pickSubstance") + " B"} value={b} onChange={(e) => setB(e.target.value)} style={selectStyle}>
            <option value="">{t("matrix.pickSubstance")}</option>
            {substances.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {selectedLevel && (() => {
            const color = LEVEL_COLORS[selectedLevel];
            const Icon = LEVEL_ICONS[selectedLevel];
            return (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  background: color + "26",
                  color,
                  border: `1px solid ${color}88`,
                  padding: "0.35rem 0.85rem",
                  borderRadius: 20,
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                <Icon size={14} />
                {selectedLabel}
              </span>
            );
          })()}
        </div>

        {/* Mobile result card (no matrix/sheet visible on mobile) */}
        {selectedLevel && (() => {
          const color = LEVEL_COLORS[selectedLevel];
          const Icon = LEVEL_ICONS[selectedLevel];
          return (
            <div
              className="sm:hidden"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.6rem",
                padding: "1rem",
                borderRadius: 12,
                background: color + "14",
                border: `1px solid ${color}55`,
              }}
            >
              <div style={{ fontSize: "1.15rem", fontWeight: 700 }}>{a} + {b}</div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "0.45rem", color, fontWeight: 700, fontSize: "0.9rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                <Icon size={16} />
                {selectedLabel}
              </div>
              {selectedLevel === "contra" && (
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color, fontWeight: 700, fontSize: "0.9rem" }}>
                  <Ban size={16} />
                  {t("matrix.noCombine")}
                </div>
              )}
            </div>
          );
        })()}

        {a && b && a === b && (
          <div style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>
            {t("matrix.pickDifferent")}
          </div>
        )}
        {noAssessment && (
          <div style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>
            {t("matrix.noAssessmentHint")}
          </div>
        )}
      </div>

      {/* Source-bound AI explanation for the selected pair */}
      <InteractionAiAnswer
        aName={aiPair?.a ?? ""}
        bName={aiPair?.b ?? ""}
        aId={idMap && aiPair ? idMap[aiPair.a] ?? null : null}
        bId={idMap && aiPair ? idMap[aiPair.b] ?? null : null}
      />

      {/* Scroll hint (visible only in 640-1023px where horizontal overflow is most likely) */}
      <div
        className="hidden sm:flex lg:hidden"
        style={{
          alignItems: "center",
          gap: "0.4rem",
          marginTop: "2rem",
          marginBottom: "0.5rem",
          fontSize: "0.75rem",
          color: "var(--text-muted)",
        }}
      >
        <ArrowLeftRight size={14} aria-hidden />
        <span>{t("matrix.scrollHint")}</span>
      </div>

      {/* Matrix - hidden on mobile */}
      <div
        className="hidden sm:block lg:mt-8"
        style={{
          overflow: "auto",
          width: "100%",
          maxHeight: "min(78vh, 50rem)",
          background: "var(--bg-primary)",
          overscrollBehavior: "contain",
        }}
      >
        <table
          style={{
            borderCollapse: "separate",
            borderSpacing: "2px",
            tableLayout: "fixed",
            width: "100%",
            minWidth: `calc(${ROW_LABEL_WIDTH}px + ${substances.length * 36}px)`,
          }}
        >
          <caption
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              overflow: "hidden",
              clip: "rect(0 0 0 0)",
              whiteSpace: "nowrap",
            }}
          >
            {t("matrix.tableDescription")}
          </caption>
          <colgroup>
            <col style={{ width: ROW_LABEL_WIDTH }} />
            {substances.map((s) => <col key={s} style={{ minWidth: 36 }} />)}
          </colgroup>
          <thead>
            <tr>
              <th
                aria-hidden="true"
                style={{
                  position: "sticky",
                  top: 0,
                  left: 0,
                  zIndex: 60,
                  minWidth: ROW_LABEL_WIDTH,
                  background: "var(--bg-primary)",
                  boxShadow: "2px 2px 0 var(--bg-primary)",
                }}
              />
              {substances.map((s, i) => (
                <th
                  key={s}
                  scope="col"
                  style={{
                    position: "sticky",
                    top: 0,
                    // Descending per column so a rotated label may overflow
                    // past its own cell border without the next column's
                    // opaque sticky background painting over it. Still below
                    // the corner cell (60) and above row headers (10).
                    zIndex: 50 - i,
                    fontSize: "0.7rem",
                    height: 82,
                    padding: "2px",
                    background: "var(--bg-primary)",
                    boxShadow: "0 2px 0 var(--bg-primary)",
                    color: "var(--text-muted)",
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                    verticalAlign: "bottom",
                    overflow: "visible",
                  }}
                >
                  <div
                    style={{
                      transform: `translate(${COLUMN_LABEL_OFFSET_X}px, 0) rotate(-45deg)`,
                      transformOrigin: "left bottom",
                      display: "inline-block",
                      width: "max-content",
                      overflow: "visible",
                      textAlign: "left",
                    }}
                  >
                    {s}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {substances.map((row) => (
              <tr key={row}>
                <th
                  scope="row"
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 10,
                    fontSize: "0.75rem",
                    textAlign: "right",
                    padding: "4px 8px",
                    color: "var(--text-muted)",
                    whiteSpace: "nowrap",
                    minWidth: ROW_LABEL_WIDTH,
                    fontWeight: 500,
                    background: "var(--bg-primary)",
                    boxShadow: "2px 0 0 var(--bg-primary)",
                  }}
                >
                  {row}
                </th>
                {substances.map((col, colIndex) => {
                  const rowIndex = substances.indexOf(row);
                  if (row === col) {
                    return <td key={col} style={{ padding: 0 }}>
                      <div
                        aria-label={`${row}: ${t("matrix.cellSelf")}`}
                        style={{ width: "100%", height: 28, borderRadius: 2, background: "rgba(255,255,255,0.02)" }}
                      />
                    </td>;
                  }
                  const entry = lookup.get(`${row}|${col}`);
                  const missing = !entry;
                  const lvl: Level = entry?.level ?? "unknown";
                  const markers = entry?.markers ?? [];
                  const markerSuffix = markers.length
                    ? ` - ${markers.map((m) => t(`marker.${m}` as StringKey)).join(", ")}`
                    : "";
                  const title = missing
                    ? `${row} + ${col}: ${t("matrix.noAssessment")}`
                    : `${row} + ${col}: ${labelFor(lvl)}${markerSuffix}`;
                  const ariaLabel = missing
                    ? `${row} plus ${col}, ${t("matrix.noAssessment")}. ${t("matrix.cellAction")}`
                    : `${row} plus ${col}, ${t("matrix.cellRisk")} ${labelFor(lvl)}${
                        markers.length
                          ? `, ${t("matrix.cellMarkers")} ${markers.map((m) => t(`marker.${m}` as StringKey)).join(", ")}`
                          : ""
                      }. ${t("matrix.cellAction")}`;
                  const Icon = LEVEL_ICONS[lvl];
                  const hasMarkers = markers.length > 0;
                  const isActive = activeCell.r === rowIndex && activeCell.c === colIndex;
                  return (
                    <td key={col} style={{ padding: 0 }}>
                      <button
                        type="button"
                        className={`rl-matrix-cell ${LEVEL_PATTERN[lvl]}`}
                        data-cell={`${rowIndex}-${colIndex}`}
                        title={title}
                        aria-label={ariaLabel}
                        tabIndex={isActive ? 0 : -1}
                        onFocus={() => setActiveCell({ r: rowIndex, c: colIndex })}
                        onKeyDown={(e) => handleCellKeyDown(e, rowIndex, colIndex)}
                        onClick={() => pickPair(row, col)}
                        style={{
                          width: "100%",
                          height: 28,
                          borderRadius: 2,
                          border: "none",
                          padding: 0,
                          background: missing
                            ? `${LEVEL_COLORS.unknown}55`
                            : lvl === "unknown"
                              ? `${LEVEL_COLORS.unknown}99`
                              : LEVEL_COLORS[lvl] + "CC",
                          cursor: "pointer",
                          transition: "opacity 0.15s",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 2,
                          color: "rgba(255,255,255,0.92)",
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = "0.8"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = "1"; }}
                      >
                        {hasMarkers
                          ? markers.map((m) => <MarkerChip key={m} m={m} size={markers.length > 1 ? 15 : 20} />)
                          : <Icon size={14} strokeWidth={2.5} aria-hidden />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

      </div>
      </div>

      <ComboInfoSheet
        open={sheetOpen}
        onOpenChange={handleSheetOpenChange}
        aId={idMap && a ? idMap[a] ?? null : null}
        bId={idMap && b ? idMap[b] ?? null : null}
        aName={a}
        bName={b}
        fallbackLabel={selectedLabel || undefined}
        fallbackColor={selectedLevel ? LEVEL_COLORS[selectedLevel] : undefined}
        forceOverlay
      />




      <div className="mt-8 grid grid-cols-1 items-stretch gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.55fr)]">
        <div
          className="glass-card"
          style={{
            padding: "1rem 1.5rem",
            display: "flex",
            alignItems: "center",
            gap: "0.6rem",
          }}
        >
          <Info size={16} style={{ color: "var(--accent-cyan)", flexShrink: 0 }} />
          <span style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>
            {t("matrix.disclaimer")}
          </span>
        </div>
        <PageSources path="/matrix" className="lg:h-full" style={{ marginTop: 0 }} />
      </div>
    </div>
  );
}
